import { useState, useEffect, useMemo, useCallback } from "react";
import useAuth from "../../../Hooks/useAuth";
import { useGetAuditLogsQuery } from "../../../store";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  Drawer,
  ListView,
  ListToolbar,
} from "../../../Components/shared";
import {
  Pagination,
  CustomDropdown,
  DateRangeField,
} from "../../../Components/atoms";
import {
  FaLayerGroup,
  FaUserShield,
  FaMicrochip,
  FaKey,
  FaCogs,
  FaListUl,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";

// Audit Log sayfası — yalnızca Admin (+ server tarafında developer).
// Contract: BayinerIOT-Workspace-/docs/api-contracts/audit-logs.md

const TYPE_LABEL = {
  USER_ACTION: "Kullanıcı",
  IOT_DEVICE: "Cihaz",
  AUTH: "Kimlik",
  API: "API",
  SYSTEM_AUTO: "Sistem",
};

const TYPE_BADGE_CLASS = {
  USER_ACTION: "bg-blue-50 text-blue-700",
  IOT_DEVICE: "bg-emerald-50 text-emerald-700",
  AUTH: "bg-amber-50 text-amber-700",
  API: "bg-purple-50 text-purple-700",
  SYSTEM_AUTO: "bg-gray-100 text-gray-600",
};

const FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "USER_ACTION", label: "Kullanıcı", icon: FaUserShield },
  { id: "IOT_DEVICE", label: "Cihaz", icon: FaMicrochip },
  { id: "AUTH", label: "Kimlik", icon: FaKey },
  { id: "SYSTEM_AUTO", label: "Sistem", icon: FaCogs },
];

const CATEGORY_OPTIONS = [
  { id: "all", value: "", label: "Tüm Kategoriler" },
  { id: "DEVICE_STATUS", value: "DEVICE_STATUS", label: "Cihaz Durumu" },
  { id: "DEVICE_OPERATION", value: "DEVICE_OPERATION", label: "Cihaz Operasyonu" },
  { id: "CREATE", value: "CREATE", label: "Oluşturma" },
  { id: "UPDATE", value: "UPDATE", label: "Güncelleme" },
  { id: "DELETE", value: "DELETE", label: "Silme" },
  { id: "LOGIN", value: "LOGIN", label: "Giriş" },
  { id: "LOGOUT", value: "LOGOUT", label: "Çıkış" },
  { id: "PASSWORD_CHANGE", value: "PASSWORD_CHANGE", label: "Parola Değişimi" },
  { id: "PERMISSION_CHANGE", value: "PERMISSION_CHANGE", label: "Yetki Değişimi" },
  { id: "VALIDATION", value: "VALIDATION", label: "Validasyon" },
  { id: "SIGNATURE", value: "SIGNATURE", label: "İmza" },
  { id: "AUTHZ", value: "AUTHZ", label: "Yetkilendirme" },
  { id: "RATE_LIMIT", value: "RATE_LIMIT", label: "Rate Limit" },
  { id: "ERROR", value: "ERROR", label: "Hata" },
  { id: "OTHER", value: "OTHER", label: "Diğer" },
];

const TABLE_COLUMNS = [
  { label: "Tarih", width: "w-40", skeletonWidth: "w-24" },
  { label: "Tip", width: "w-24", skeletonWidth: "w-16" },
  { label: "Kategori", width: "w-36", skeletonWidth: "w-24" },
  { label: "İşlem", width: "", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "Kullanıcı", width: "w-48", skeletonWidth: "w-28" },
  { label: "Cihaz", width: "w-36", skeletonWidth: "w-24" },
  { label: "Durum", width: "w-20", skeletonWidth: "w-12" },
];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

function TypeBadge({ type }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-SemiBold ${
        TYPE_BADGE_CLASS[type] || "bg-gray-100 text-gray-600"
      }`}
    >
      {TYPE_LABEL[type] || type || "-"}
    </span>
  );
}

function SuccessBadge({ success }) {
  return success === false ? (
    <FaTimesCircle className="w-4 h-4 text-red-500" title="Başarısız" />
  ) : (
    <FaCheckCircle className="w-4 h-4 text-emerald-500" title="Başarılı" />
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-Medium text-right break-words">
        {value}
      </span>
    </div>
  );
}

function AuditDetailDrawer({ log, onClose }) {
  return (
    <Drawer
      open={!!log}
      onClose={onClose}
      width="md"
      title="Audit Log Detayı"
      subtitle={log?.action || ""}
      titleIcon={<FaListUl className="w-5 h-5 text-main" />}
    >
      {log && (
        <Drawer.Body className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <TypeBadge type={log.type} />
            <SuccessBadge success={log.success} />
          </div>

          <div>
            <h4 className="text-xs font-SemiBold text-gray-400 tracking-wider mb-1">
              İŞLEM
            </h4>
            <DetailRow label="İşlem" value={log.action} />
            <DetailRow label="Kategori" value={log.category} />
            <DetailRow label="Açıklama" value={log.description} />
            <DetailRow label="Tarih" value={formatDate(log.timestamp)} />
          </div>

          {(log.userEmail || log.userRole) && (
            <div>
              <h4 className="text-xs font-SemiBold text-gray-400 tracking-wider mb-1">
                KULLANICI
              </h4>
              <DetailRow label="E-posta" value={log.userEmail} />
              <DetailRow label="Rol" value={log.userRole} />
              <DetailRow label="IP" value={log.ipAddress} />
              <DetailRow label="Endpoint" value={log.endpoint} />
              <DetailRow label="Metot" value={log.method} />
            </div>
          )}

          {(log.deviceSerialNo || log.resourceType) && (
            <div>
              <h4 className="text-xs font-SemiBold text-gray-400 tracking-wider mb-1">
                KAYNAK
              </h4>
              <DetailRow label="Cihaz Seri No" value={log.deviceSerialNo} />
              <DetailRow label="Kaynak Tipi" value={log.resourceType} />
              <DetailRow label="Kaynak ID" value={log.resourceId} />
            </div>
          )}

          {log.error?.message && (
            <div className="bg-red-50 rounded-xl p-3">
              <h4 className="text-xs font-SemiBold text-red-500 tracking-wider mb-1">
                HATA
              </h4>
              <DetailRow label="Mesaj" value={log.error.message} />
              <DetailRow label="Kod" value={log.error.code} />
            </div>
          )}

          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <h4 className="text-xs font-SemiBold text-gray-400 tracking-wider mb-2">
                METADATA
              </h4>
              <pre className="bg-gray-50 rounded-xl p-3 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
        </Drawer.Body>
      )}
    </Drawer>
  );
}

function AuditLogPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const pageSize = 25;

  const [activeFilter, setActiveFilter] = useState("all");
  const [category, setCategory] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [serialInput, setSerialInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [debounced, setDebounced] = useState({
    serialNo: "",
    userEmail: "",
    action: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);

  // Arama debounce (üç alan tek pakette)
  useEffect(() => {
    const t = setTimeout(
      () =>
        setDebounced({
          serialNo: serialInput.trim(),
          userEmail: emailInput.trim(),
          action: actionInput.trim(),
        }),
      400,
    );
    return () => clearTimeout(t);
  }, [serialInput, emailInput, actionInput]);

  // Filtre değişince ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, category, startDate, endDate, debounced]);

  const endOfDay = (d) => {
    const dt = new Date(d);
    dt.setHours(23, 59, 59, 999);
    return dt.toISOString();
  };

  const queryArgs = useMemo(
    () => ({
      token,
      page: currentPage,
      limit: pageSize,
      type: activeFilter === "all" ? undefined : activeFilter,
      category: category || undefined,
      serialNo: debounced.serialNo || undefined,
      userEmail: debounced.userEmail || undefined,
      action: debounced.action || undefined,
      startDate: startDate ? new Date(startDate).toISOString() : undefined,
      endDate: endDate ? endOfDay(endDate) : undefined,
    }),
    [token, currentPage, activeFilter, category, debounced, startDate, endDate],
  );

  const { data: apiData, isLoading, isFetching } = useGetAuditLogsQuery(
    queryArgs,
    { skip: !token },
  );

  const logs = apiData?.data?.logs || [];
  const total = apiData?.data?.total || 0;
  const totalPages = apiData?.data?.totalPages || 1;
  const loading = isLoading || isFetching;

  const stats = useMemo(
    () => [{ icon: FaListUl, value: total, label: "Kayıt", color: "blue" }],
    [total],
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) tableContainer.scrollTop = 0;
  }, []);

  const extraFiltersNode = (
    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:items-end">
      <div className="sm:w-48">
        <CustomDropdown
          label="Kategori"
          compact={true}
          value={
            CATEGORY_OPTIONS.find((o) => o.value === category) ||
            CATEGORY_OPTIONS[0]
          }
          onChange={(v) => setCategory(v.value)}
          options={CATEGORY_OPTIONS}
          placeholder="Kategori seçin"
          showSearch={false}
        />
      </div>
      <DateRangeField
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        size="sm"
        zIndex={10050}
        className="flex flex-col sm:flex-row gap-2 [&>div]:sm:w-44"
      />
    </div>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <PageShell>
        <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
          <PageHeader
            icon={FaUserShield}
            iconColor="blue"
            title="Audit Log"
            subtitle="Sistem genelindeki denetim kayıtları — kim, ne zaman, ne yaptı"
            stats={stats}
            isLoading={loading}
            mobileSlotId="audit-log-toolbar-mobile-slot"
          />
        </div>

        <div className="bg-white flex-1 min-h-0 flex flex-col">
          <ListToolbar
            className="px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
            mobileFilterSlotId="audit-log-toolbar-mobile-slot"
            filterTabs={{
              filters: FILTERS,
              activeFilter,
              onChange: setActiveFilter,
              totalCount: total,
              isLoading: loading,
              color: "blue",
            }}
            search={extraFiltersNode}
            searchFields={[
              {
                placeholder: "Cihaz Seri No",
                value: serialInput,
                onChange: (e) => setSerialInput(e.target.value),
              },
              {
                placeholder: "Kullanıcı E-posta",
                value: emailInput,
                onChange: (e) => setEmailInput(e.target.value),
              },
              {
                placeholder: "İşlem adı (örn. setDeviceQuotaPush)",
                value: actionInput,
                onChange: (e) => setActionInput(e.target.value),
              },
            ]}
            onClear={() => {
              setActiveFilter("all");
              setCategory("");
              setStartDate(null);
              setEndDate(null);
              setSerialInput("");
              setEmailInput("");
              setActionInput("");
              setCurrentPage(1);
            }}
          />

          <ListView
            headers={TABLE_COLUMNS.map((c) => ({
              label: c.label,
              className: c.width,
            }))}
            data={logs}
            rowKey="_id"
            renderRow={(log) => (
              <tr
                key={log._id}
                className="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelected(log)}
              >
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatDate(log.timestamp)}
                </td>
                <td className="px-4 py-3">
                  <TypeBadge type={log.type} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {log.category || "-"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-800 font-Medium truncate">
                  {log.action || "-"}
                  {log.description && (
                    <div className="text-xs text-gray-400 font-Regular truncate">
                      {log.description}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 truncate">
                  {log.userEmail || "-"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono truncate">
                  {log.deviceSerialNo || "-"}
                </td>
                <td className="px-4 py-3">
                  <SuccessBadge success={log.success} />
                </td>
              </tr>
            )}
            renderMobileCard={(log) => (
              <div
                className="bg-white px-3.5 py-3 border-b border-gray-200 active:bg-gray-50 cursor-pointer"
                onClick={() => setSelected(log)}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <TypeBadge type={log.type} />
                  <SuccessBadge success={log.success} />
                </div>
                <div className="text-sm text-gray-800 font-Medium truncate">
                  {log.action || "-"}
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                  <span className="truncate">
                    {log.userEmail || log.deviceSerialNo || log.category || "-"}
                  </span>
                  <span>{formatDate(log.timestamp)}</span>
                </div>
              </div>
            )}
            isLoading={loading}
            loadingNode={
              <div className="relative p-3 sm:p-4 md:p-6">
                <ListSkeleton
                  columns={TABLE_COLUMNS}
                  rowCount={10}
                  mobileCardCount={6}
                  message="Audit logları yükleniyor..."
                />
              </div>
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  message="Kayıt bulunamadı"
                  suggestion="Seçili filtrelere uygun audit kaydı yok."
                />
              </div>
            }
            footer={
              totalPages > 1 ? (
                <div className="shrink-0 border-t border-gray-200 bg-white">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    showInfo={true}
                    disabled={loading}
                  />
                </div>
              ) : null
            }
          />
        </div>

        <AuditDetailDrawer log={selected} onClose={() => setSelected(null)} />
      </PageShell>
    </LocalizationProvider>
  );
}

export default AuditLogPage;
